/* eslint-disable no-case-declarations */
import React, { useEffect, useCallback, useState } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { MemberCreateRequest } from '../../../@types/Members';
import { useMemberDetails } from '../../../hooks/useMemberDetail';
import { transformOutcomingMemberCreateRequest } from '../../../utils/members/outcomingDataTransformationHelper';
import { defaultMemberState } from '../../../utils/members/validationSchema';
import MemberFormContent from './MemberFormContent';

const TOAST_DEFAULT_OPTIONS: ToastOptions = { position: 'top-center', closeButton: false };
interface ManageMemberProps {
  id: string | null;
  onRequestClose: (reset: boolean) => void;
  fetchMembers: ({ search, skip, limit }: any) => Promise<any>;
}
function ManageMember({ id, onRequestClose, fetchMembers }: ManageMemberProps) {
  const isNewMemberFlow: number | boolean = id === null;
  const [memberId, setMemberId] = useState(id);
  const [additionalQueriesInProgress, setAdditionalQueriesInProgress] = useState<boolean>(false);
  const {
    member: existingMember,
    createMember,
    updateMemberV2,
    isMemberMutationLoading,
    loadDependentMembers,
    createDependentMember
  } = useMemberDetails({ memberId: memberId });

  const saveHandler = useCallback(
    async (data, isPrimary, isCreate, originMember, setFormValue) => {
      let response;
      const action: string = isCreate ? 'create' : 'update';
      const role: string = isPrimary ? 'Member' : 'Dependent';
      try {
        switch (true) {
          case isPrimary && isCreate:
            const transformedMember: MemberCreateRequest = transformOutcomingMemberCreateRequest(data);
            response = { result: await createMember(transformedMember) };
            break;
          case !isPrimary && isCreate:
            response = await createDependentMember({
              primary: { ...originMember },
              dependent: data
            });
            if (response && response.result && !response.result.errors?.length) {
              setAdditionalQueriesInProgress(true);
              setFormValue('relatedInstances', await loadDependentMembers());
              setAdditionalQueriesInProgress(false);
            }
            break;
          default:
            response = await updateMemberV2({ primary: { ...originMember }, memberToUpdate: data });
        }
        if (response?.newPrimaryId) {
          setMemberId(response.newPrimaryId);
        }
        if (response && !response.result.errors?.length) {
          toast.success(`${role} ${action}d successfully`, { position: 'top-center' });
          onRequestClose(true); // this results in a close of the modal
        }
      } catch {
        toast.error(`Failed to ${action} ${role}`, TOAST_DEFAULT_OPTIONS);
      }
    },
    [createDependentMember, createMember, updateMemberV2, onRequestClose, loadDependentMembers]
  );

  useEffect(() => {
    if (existingMember.isError && !isNewMemberFlow) toast.error('Failed to get member data', TOAST_DEFAULT_OPTIONS);
  }, [isNewMemberFlow, existingMember.isError]);

  return (
    <MemberFormContent
      defaultValues={existingMember.data || defaultMemberState}
      isCreate={!memberId}
      onCancel={onRequestClose}
      onSave={saveHandler}
      isLoading={isMemberMutationLoading || additionalQueriesInProgress}
      fetchMembers={fetchMembers}
    />
  );
}

export default ManageMember;
